import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Products() {
  const { user } = useAuth();
  // Branch users (admin/supervisor/employee) get a read-only view — central CRUD lives at /super/products
  const isReadOnly = user?.role !== 'super_admin';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({ name: '', price: '' });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('فشل تحميل المنتجات');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = { name: formData.name, price: parseFloat(formData.price) || 0 };
      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, payload);
        toast.success('تم تحديث المنتج بنجاح');
      } else {
        await axios.post(`${API_URL}/api/products`, payload);
        toast.success('تم إضافة المنتج بنجاح');
      }
      
      setOpen(false);
      setFormData({ name: '', price: '' });
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حفظ المنتج');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({ name: product.name, price: (product.price ?? 0).toString() });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) return;
    
    try {
      await axios.delete(`${API_URL}/api/products/${id}`);
      toast.success('تم حذف المنتج بنجاح');
      fetchProducts();
    } catch (error) {
      toast.error('فشل حذف المنتج');
    }
  };

  if (loading) {
    return <div className="text-center py-12">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-primary">إدارة المنتجات</h1>
          <p className="text-muted-foreground mt-1">
            {isReadOnly
              ? 'القائمة موحّدة من المدير العام — للتعديل تواصل معه'
              : 'أضف وعدّل المنتجات'}
          </p>
        </div>
        {!isReadOnly && (
        <Dialog open={open} onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) {
            setEditingProduct(null);
            setFormData({ name: '', price: '' });
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-product-button" className="gap-2">
              <Plus className="h-4 w-4" />
              إضافة منتج
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[400px]" dir="rtl">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'تعديل منتج' : 'إضافة منتج جديد'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">اسم المنتج</Label>
                <Input
                  id="name"
                  data-testid="product-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="text-right"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">السعر <SAR /></Label>
                <Input
                  id="price"
                  data-testid="product-price-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="text-right font-numbers"
                  required
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  إلغاء
                </Button>
                <Button type="submit" data-testid="save-product-button">
                  {editingProduct ? 'تحديث' : 'حفظ'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
        {isReadOnly && (
          <div className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold" data-testid="products-readonly-badge">
            <Lock className="h-3.5 w-3.5" />
            عرض فقط — إدارة مركزية
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {products.length === 0 ? (
          <Card className="col-span-full p-12 text-center border-border">
            <p className="text-muted-foreground">لا توجد منتجات</p>
          </Card>
        ) : (
          products.map((product) => (
            <Card key={product.id} className="p-6 border-border hover:shadow-md transition-all">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-foreground">{product.name}</h3>
                  <p className="text-2xl font-black text-brand_blue font-numbers mt-2">
                    {(product.price ?? 0).toLocaleString('en-US')} <SAR />
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(product.created_at).toLocaleDateString('ar-SA', { numberingSystem: 'latn', calendar: 'gregory' })}
                  </p>
                </div>
                {!isReadOnly && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(product)}
                    data-testid={`edit-product-${product.id}`}
                    className="flex-1 gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    تعديل
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(product.id)}
                    data-testid={`delete-product-${product.id}`}
                    className="flex-1 gap-2 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                    حذف
                  </Button>
                </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}